import { Chatbot, Grid, Modal, UI, Utils } from 'common';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import isNull from 'lodash/isNull';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import remove from 'lodash/remove';
import React from 'react';
import AccountCommunication from '../utils/AccountCommunication';
import AuctanePayCommunication from '../utils/AuctanePayCommunication';
import { RoutePath } from '../utils/RoutePath';
import AccountUtil from '../utils/Util';
import { ActionType } from './ActionType.enum';
import DeletePaymentModal from './DeletePaymentModal.component';
import PBBRedirectHandler from './fsui/PBBRedirectHandler';
import ManagePaymentModal from './ManagePaymentModal.component';
import { PaymentAccountType as PaymentMethodType } from './PaymentAccountType.enum';

const { ModalButtonActionType } = Modal;
const { WrappedToolbar, Spinner, WrappedButtonType } = UI;
const { GridPanel } = Grid;
const { Logger, withLayoutAndModalContexts, UserRole, Session, Util } = Utils;
const { CREATE, EDIT } = ActionType;
const { ChatbotTriggers, launchChatbot } = Chatbot;

class PaymentMethods extends React.Component {
	constructor(props) {
		super(props);

		this.gridApi = null;

		const isForCorpPaymentAccount = this.isForCorpPaymentAccount();

		this.state = {
			selectedNodes: [],
			rowData: [],
			paymentAccountForChargeCategory: {},
			/**
			 * If user is a corp admin (or a region admin), there are two account data user
			 * can see, corporation account and meter account.
			 *
			 * System fetches the corporation account data when user clicks "Payment methods"
			 * tab under "Corporate Settings" (or "Region Settings" for a region admin) as
			 * user is mananging the methods for the entire corporation (or the region).
			 *
			 * System fetches the meter account data when user clicks "Payment methods" tab
			 * under "My Location Settings" or "Default Payment" through the location
			 * setting dialog as user is managing the methods for the location he or she is
			 * assigned to.
			 */
			isForCorpPaymentAccount,
			isLoading: false,
			loadingMessage: '',
			useAuctanePay: props?.flags?.useAuctanePay,
			showAuctanePay: false,
		};

		this.completePayByBankRedirect = false;

		const baseColumnDefs = [
			{
				headerName: '',
				field: 'checkbox',
				width: 50,
				checkboxSelection: true,
			},
			{
				headerName: 'Nickname',
				field: 'NickName',
				minWidth: 50,
				width: 150,
				cellRenderer: param => {
					const link = document.createElement('a');
					link.href = '/';
					link.innerText = param.value;
					link.addEventListener('click', e => {
						e.preventDefault();
						this.onSelectionChanged([param.node]);
						this.launchEditModal();
					});
					return link;
				},
			},
			{
				headerName: 'Type',
				field: 'AccountType',
				minWidth: 50,
				width: 150,
			},
			{
				headerName: 'Last 4',
				field: 'AccountNumberDisplayDigits',
				minWidth: 50,
				width: 100,
			},
			{
				headerName: 'Expiration',
				field: 'ExpiryDate',
				minWidth: 50,
				width: 200,
			},
			{
				headerName: 'Cardholder Name',
				field: 'AccountHolderName',
				minWidth: 50,
				width: 200,
			},
			{
				headerName: 'Billing Address',
				field: 'BillingAddress.Address1',
				minWidth: 50,
				width: 200,
			},
			{
				headerName: 'Billing City',
				field: 'BillingAddress.City',
				minWidth: 50,
				width: 200,
			},
			{
				headerName: 'Billing State',
				field: 'BillingAddress.State',
				minWidth: 50,
				width: 200,
			},
			{
				headerName: 'Billing ZIP',
				field: 'BillingAddress.Zip',
				minWidth: 50,
				width: 200,
			},
		];
		this.columnDefs = baseColumnDefs;
	}

	componentDidMount() {
		const sessionId = Util.getQueryParamByName('sessionId') || null;
		const redirectResult = Util.getQueryParamByName('redirectResult') || null;

		this.completePayByBankRedirect =
			isString(sessionId) && isString(redirectResult);
	}

	componentDidUpdate(prevProps, prevState) {
		const { showAuctanePay } = this.state;

		if (
			prevState?.showAuctanePay !== showAuctanePay &&
			showAuctanePay &&
			this.gridApi
		) {
			const apColumnDefs = [
				{
					headerName: '',
					field: 'checkbox',
					width: 50,
					checkboxSelection: true,
				},
				{
					headerName: 'Nickname',
					field: 'NickName', // will have to update the field name later
					minWidth: 50,
					flex: 1,
				},
				{
					headerName: 'Type',
					field: 'AccountType',
					minWidth: 50,
					flex: 1,
					cellRenderer: ({ data = {} }) => {
						const { AccountType = '', PaymentAccountType = 0 } = data;
						const icon =
							PaymentAccountType === PaymentMethodType.ACH
								? 'ach'
								: AccountType.toLowerCase();

						let type = AccountType;

						if (PaymentAccountType === PaymentMethodType.ACH) {
							type = 'Pay by Bank';
						}

						return `
						  <span class="payment-icon ${icon}">
							<span class="icon-placeholder"></span>${type}
						  </span>
						`;
					},
				},
				{
					headerName: 'Last 4',
					field: 'AccountNumberDisplayDigits',
					minWidth: 50,
					flex: 1,
				},
				{
					headerName: 'Billing Address',
					minWidth: 50,
					flex: 1,
					valueGetter: params => {
						const address = params.data?.BillingAddress || {};
						const parts = [address.Address1, address.Address2].filter(Boolean);
						return parts.join(', ');
					},
				},
			];

			this.columnDefs = apColumnDefs;
			this.gridApi.setColumnDefs(this.columnDefs);
		}
	}

	/**
	 * Return `true` if system should fetch corporation payment account data.
	 * @returns {boolean}
	 */
	isForCorpPaymentAccount = () => {
		const { location } = this.props;

		// get user role
		const userRole = Session.getUserRole();

		// check on URL path, '/paymentmethods' or '/locationpaymentmethods'
		const isPathLocationPaymentMethods =
			location && location.pathname === RoutePath.locationPaymentMethods;

		return (
			(userRole === UserRole.CorpAdmin || userRole === UserRole.RegionAdmin) &&
			!isPathLocationPaymentMethods
		);
	};

	// callback to get payment account
	callback = ({ status }) => {
		if (status) {
			const { isForCorpPaymentAccount } = this.state;

			this.displayLoadingSpinner(true, 'Updating payment methods...');

			if (isForCorpPaymentAccount) {
				this.getCorporationPaymentAccounts();
			} else {
				this.getPaymentAccounts();
			}
		}
	};

	deletePaymentMethod = async (selectedIds = [], completeCallback) => {
		const {
			modalContext: { showError },
		} = this.props;
		const { isForCorpPaymentAccount } = this.state;

		if (selectedIds.length > 0) {
			const [selectedId] = selectedIds.splice(0, 1);
			const response = isForCorpPaymentAccount
				? await AccountCommunication.deleteCorporationPaymentAccount(selectedId)
				: await AccountCommunication.deletePaymentAccount(selectedId);

			if (AccountCommunication.isSuccessful(response)) {
				this.deletePaymentMethod(selectedIds, completeCallback);
			} else {
				showError({ response });
				this.displayLoadingSpinner(false);
			}
		} else {
			completeCallback();
		}
	};

	deletePaymentMethods = () => {
		const {
			modalContext: { showError },
		} = this.props;
		const { selectedNodes, paymentAccountForChargeCategory } = this.state;
		const associatedPaymentIds = Object.values(paymentAccountForChargeCategory);
		const selectedIds = selectedNodes.map(
			item => item.data && item.data.PaymentAccountId
		);
		const paymentsInUse = selectedIds.filter(item =>
			associatedPaymentIds.includes(item)
		);

		Logger.saveActionData('Delete Payment Button Clicked');

		// no selected payment methods are currently in use
		if (paymentsInUse.length === 0) {
			this.displayLoadingSpinner(true, 'Updating payment methods...');

			this.deletePaymentMethod(selectedIds, () => {
				// get updated payments
				this.callback({ status: true });
			});
		}
		// selected payment is in used
		else {
			showError({
				data: {
					title: 'Cannot Delete Payment Method',
					description:
						'A selected payment method is being used in the Payment Settings page. Please make sure the payment method is not used in order to delete it.',
					primaryButtonType: ModalButtonActionType.Ok,
					primaryButtonText: 'Close',
					handleType: 'custom',
				},
			});
		}
	};

	displayLoadingSpinner = (isLoading = true, loadingMessage = '') => {
		this.setState({
			isLoading,
			loadingMessage,
		});
	};

	getApPaymentAccounts = async () => {
		const {
			modalContext: { showError },
		} = this.props;
		const { customerId } = Session.getAccountInfo();

		const response = await AuctanePayCommunication.getAPSession({ customerId });

		this.displayLoadingSpinner(false);

		if (AccountCommunication.isSuccessful(response)) {
			const { activePaymentMethods = [], recentPaymentMethods = [] } = isObject(
				response.data
			)
				? response.data
				: {};
			const apPaymentMethods = [
				...activePaymentMethods,
				...recentPaymentMethods,
			].reduce((acc, item) => {
				if (!acc[item.id]) {
					acc[item.id] = item;
				}

				return acc;
			}, {});

			this.setState({
				rowData: Object.values(apPaymentMethods),
				selectedNodes: [],
			});
		} else {
			showError({ response });
		}
	};

	getPaymentAccounts = async () => {
		const {
			modalContext: { showError },
		} = this.props;
		const { useAuctanePay } = this.state;

		const response = await AccountCommunication.getPaymentAccounts();

		this.displayLoadingSpinner(false);

		if (AccountCommunication.isSuccessful(response)) {
			const data = isObject(response.data) ? response.data : {};
			const { PaymentAccounts = [], PaymentAccountForChargeCategory = {} } =
				data;
			const newRowData = PaymentAccounts.map(account => {
				if (useAuctanePay) {
					return account;
				}
				if (account.PaymentAccountType !== PaymentMethodType.ACH) {
					return account;
				}
				return null;
			});

			remove(newRowData, isNull);

			this.setState({
				rowData: newRowData,
				paymentAccountForChargeCategory: PaymentAccountForChargeCategory,
				selectedNodes: [],
			});

			const id = newRowData[0]?.PaymentAccountId;
			const isAuctanePayAccount =
				AccountUtil.hasAccountMigratedToAuctanePay(id);

			if (isAuctanePayAccount && useAuctanePay) {
				this.setState({
					showAuctanePay: true,
				});
			}

			// Assume the account is migrated to Auctane Pay if it has no payment methods. This can happen for user-created accounts.
			if (PaymentAccounts.length === 0 && useAuctanePay) {
				this.setState({
					showAuctanePay: true,
				});
			}
		} else {
			showError({ response });
		}
	};

	getCorporationPaymentAccounts = async () => {
		const {
			modalContext: { showError },
		} = this.props;
		const { useAuctanePay } = this.state;

		const response = await AccountCommunication.getCorporationPaymentAccounts();

		// If response is null, it means the account is migrated to Auctane Pay and has no corporation payment accounts
		if (response.data === null) {
			this.setState({
				showAuctanePay: true,
			});
		}

		this.displayLoadingSpinner(false);

		if (AccountCommunication.isSuccessful(response)) {
			const data = isObject(response.data) ? response.data : {};
			const { PaymentAccounts = [], PaymentAccountForChargeCategory = {} } =
				data;

			this.setState({
				rowData: PaymentAccounts,
				paymentAccountForChargeCategory: PaymentAccountForChargeCategory,
				selectedNodes: [],
			});

			const id = PaymentAccounts[0]?.PaymentAccountId;
			const isAuctanePayAccount =
				AccountUtil.hasAccountMigratedToAuctanePay(id);

			if (isAuctanePayAccount && useAuctanePay) {
				this.setState({
					showAuctanePay: true,
				});
			}
		} else {
			showError({ response });
		}
	};

	launchAddModal = () => {
		const {
			modalContext: { showModal },
		} = this.props;
		const { rowData, isForCorpPaymentAccount, showAuctanePay } = this.state;

		Logger.logToServer({
			category: 'ui_interaction',
			action: 'click',
			ui_element: 'add_button',
			module: 'ui_add_payment_dialog_onshow',
		});

		showModal({
			component: ManagePaymentModal,
			data: {
				action: CREATE,
				callback: this.callback,
				allAccounts: rowData,
				isCorpAccount: isForCorpPaymentAccount,
				showAuctanePay,
			},
		});
	};

	launchEditModal = () => {
		const {
			modalContext: { showModal },
		} = this.props;
		const {
			rowData,
			selectedNodes,
			isForCorpPaymentAccount,
			paymentAccountForChargeCategory,
		} = this.state;
		const { data: paymentInfo } = selectedNodes[0];

		Logger.logToServer({
			category: 'ui_interaction',
			action: 'click',
			ui_element: 'edit_button',
			module: 'ui_edit_payment_dialog_onshow',
		});

		showModal({
			component: ManagePaymentModal,
			data: {
				paymentInfo,
				paymentAccountForChargeCategory,
				action: EDIT,
				allAccounts: rowData,
				callback: this.callback,
				isCorpAccount: isForCorpPaymentAccount,
			},
		});
	};

	launchDeleteModal = () => {
		const {
			modalContext: { showModal },
		} = this.props;
		const { selectedNodes = [] } = this.state;

		Logger.logToServer({
			category: 'ui_interaction',
			action: 'click',
			ui_element: 'delete_button',
			module: 'ui_delete_payment_dialog_onshow',
		});

		showModal({
			component: DeletePaymentModal,
			data: {
				selectedCount: selectedNodes.length,
			},
			closeCallback: closeType => {
				if (ModalButtonActionType.Confirm === closeType) {
					this.deletePaymentMethods();
				}
			},
		});
	};

	launchFeedback = () => {
		const {
			modalContext: { showModal },
		} = this.props;

		launchChatbot({
			flags: this.props.flags,
			showModal,
			trigger: ChatbotTriggers.FeedbackButton,
		});
	};

	onGridReady = ({ api }) => {
		this.gridApi = api;
		const { isForCorpPaymentAccount } = this.state;

		this.displayLoadingSpinner(true, 'Loading payment methods...');

		if (isForCorpPaymentAccount) {
			this.getCorporationPaymentAccounts();
		} else {
			this.getPaymentAccounts();
		}
	};

	onSelectionChanged = param => {
		this.setState({
			selectedNodes: param,
		});
	};

	onSortChanged = sortModel => {
		this.gridApi.setSortModel(sortModel);
	};

	render() {
		const {
			rowData,
			selectedNodes,
			isLoading,
			loadingMessage,
			showAuctanePay,
		} = this.state;
		let buttons = [
			{
				label: 'Add',
				icon: 'add-icon',
				className: 'icon-btn',
				onClick: this.launchAddModal,
				tooltip: `${'Add'.bold()}\nAdd a new Payment Method`,
				tooltipOptions: {
					position: 'right',
					className: 'p-tooltip-expand-width',
				},
			},
		];

		if (rowData.length > 0) {
			buttons = [
				...buttons,
				{
					label: 'Delete',
					onClick: this.launchDeleteModal,
					tooltip: `${'Delete'.bold()}\nDelete a Payment Method`,
					tooltipOptions: {
						position: 'right',
						className: 'p-tooltip-expand-width',
					},
					disabled: selectedNodes.length === 0,
				},
				{
					icon: 'feedback-icon',
					buttonType: WrappedButtonType.TertiaryWhite,
					label: 'Feedback',
					group: 'right',
					onClick: this.launchFeedback,
					tooltip: `${'Feedback'.bold()}\nTell us how we're doing!`,
					tooltipOptions: {
						position: 'right',
						className: 'p-tooltip-expand-width',
					},
				},
			];

			if (!showAuctanePay) {
				buttons.splice(1, 0, {
					label: 'Edit',
					onClick: this.launchEditModal,
					tooltip: `${'Edit'.bold()}\nEdit the selected Payment Method`,
					tooltipOptions: {
						position: 'right',
						className: 'p-tooltip-expand-width',
					},
					disabled: selectedNodes.length !== 1,
				});
			}
		}

		return (
			<div className="account-payment payment-methods">
				<div className="payment-methods-toolbar">
					<WrappedToolbar buttons={buttons} dataqa="payment-methods-toolbar" />
				</div>
				<div className="payment-methods-grid">
					<GridPanel
						rowData={rowData}
						showPagination={false}
						columnDefs={this.columnDefs}
						onGridReady={this.onGridReady}
						onSortChanged={this.onSortChanged}
						onSelectionChanged={this.onSelectionChanged}
						dataqa="payment-methods-grid"
					/>
				</div>
				{isLoading && <Spinner msg={loadingMessage} />}
				{this.completePayByBankRedirect && <PBBRedirectHandler />}
			</div>
		);
	}
}

const PaymentMethodsWithLayoutAndModal =
	withLayoutAndModalContexts(PaymentMethods);

export default withLDConsumer()(PaymentMethodsWithLayoutAndModal);
